import { Link } from 'react-router'
import { SITE_NAME } from '#app/utils/branding.ts'
import { type Route } from './+types/privacy.ts'

const LAST_UPDATED = 'March 1, 2026'
const CONTACT_EMAIL = 'support@ikonexamen.com'

export const meta: Route.MetaFunction = () => [
	{ title: `Privacy Policy - ${SITE_NAME}` },
	{
		name: 'description',
		content: `Read how ${SITE_NAME} collects, uses, stores, and protects your information.`,
	},
]

export default function PrivacyRoute() {
	return (
		<main className="bg-cathedral-page min-h-screen px-6 py-16 md:py-24">
			<article className="bg-cathedral-card mx-auto w-full max-w-4xl rounded-xl border p-8 md:p-12">
				<header className="border-cathedral-gold/20 border-b pb-6">
					<p className="cathedral-heading text-cathedral-gold text-xs tracking-[0.14em] uppercase">
						{SITE_NAME}
					</p>
					<h1 className="cathedral-heading text-cathedral-dark mt-2 text-4xl font-semibold tracking-wide md:text-5xl">
						Privacy Policy
					</h1>
					<p className="cathedral-body text-cathedral-brown mt-3 text-sm">
						Last updated: {LAST_UPDATED}
					</p>
				</header>

				<div className="cathedral-body text-cathedral-brown mt-8 space-y-8 text-base leading-relaxed md:text-lg">
					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							1. Scope
						</h2>
						<p className="mt-3">
							This Privacy Policy explains how {SITE_NAME} collects, uses,
							stores, and shares personal information when you visit our
							website, create an account, purchase an assessment, take an
							assessment, or interact with our support team.
						</p>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							2. Information We Collect
						</h2>
						<ul className="mt-3 list-disc space-y-2 pl-5">
							<li>
								Account details, such as email address, username, name,
								authentication data, and optional profile details you choose to
								provide, such as age, gender, or contact opt-in preference.
							</li>
							<li>
								Assessment data, including responses, scores, generated
								narratives, and progress history.
							</li>
							<li>
								Access and transaction data, such as purchase records, Stripe
								payment identifiers, campaign access-code redemptions, and
								related attempt grants. We do not store full card numbers.
							</li>
							<li>
								Technical and usage data, such as IP address, device/browser
								metadata, and interaction logs needed for security, reliability,
								and analytics.
							</li>
							<li>
								Support communications you send to us by email or through
								product feedback channels.
							</li>
						</ul>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							3. How We Use Information
						</h2>
						<ul className="mt-3 list-disc space-y-2 pl-5">
							<li>Provide and improve the assessment experience.</li>
							<li>Operate accounts, authentication, and customer support.</li>
							<li>Process purchases, prevent fraud, and maintain security.</li>
							<li>
								Send Image and Likeness retreat, event, and future IKON Examen
								updates only when you opt in.
							</li>
							<li>
								Generate and deliver assessment output, including AI-assisted
								narratives.
							</li>
							<li>
								Comply with legal obligations and enforce our Terms of Service.
							</li>
						</ul>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							4. Legal Bases for Processing
						</h2>
						<p className="mt-3">
							Where applicable, we process information based on contract
							performance (providing purchased services), legitimate interests
							(security and product improvement), legal obligations, and consent
							(where required).
						</p>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							5. Sharing of Information
						</h2>
						<p className="mt-3">
							We share information only as needed to operate the service, such
							as with infrastructure, payment, email, authentication, and AI
							service providers. We may also disclose information when required
							by law, legal process, or to protect rights, safety, and platform
							integrity.
						</p>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							6. Data Retention
						</h2>
						<p className="mt-3">
							We retain personal information for as long as reasonably necessary
							to provide the service, comply with legal and accounting
							obligations, resolve disputes, and enforce agreements. Retention
							periods vary by data type and legal requirements.
						</p>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							7. Your Privacy Rights
						</h2>
						<p className="mt-3">
							Depending on your location, you may have rights to access,
							correct, delete, or export your data, and to object to or restrict
							certain processing. You may request account data export from the
							platform or contact us at{' '}
							<a
								href={`mailto:${CONTACT_EMAIL}`}
								className="text-cathedral-gold hover:text-cathedral-gold-light underline"
							>
								{CONTACT_EMAIL}
							</a>
							.
						</p>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							8. Security
						</h2>
						<p className="mt-3">
							We use administrative, technical, and organizational safeguards
							designed to protect personal information. No system is perfectly
							secure, and we cannot guarantee absolute security.
						</p>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							9. Children&apos;s Privacy
						</h2>
						<p className="mt-3">
							The service is not directed to children under 13 (or older where
							required by local law). If you believe a child provided personal
							information to us, contact{' '}
							<a
								href={`mailto:${CONTACT_EMAIL}`}
								className="text-cathedral-gold hover:text-cathedral-gold-light underline"
							>
								{CONTACT_EMAIL}
							</a>
							.
						</p>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							10. International Transfers
						</h2>
						<p className="mt-3">
							Your information may be processed in countries other than your
							own. Where required, we use appropriate safeguards for
							cross-border data transfers.
						</p>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							11. Changes to This Policy
						</h2>
						<p className="mt-3">
							We may update this Privacy Policy from time to time. Material
							changes will be reflected by updating the &quot;Last updated&quot;
							date on this page.
						</p>
					</section>

					<section>
						<h2 className="cathedral-heading text-cathedral-dark text-xl font-semibold md:text-2xl">
							12. Contact
						</h2>
						<p className="mt-3">
							For privacy questions or requests, email{' '}
							<a
								href={`mailto:${CONTACT_EMAIL}`}
								className="text-cathedral-gold hover:text-cathedral-gold-light underline"
							>
								{CONTACT_EMAIL}
							</a>
							.
						</p>
					</section>
				</div>

				<footer className="border-cathedral-gold/20 mt-10 border-t pt-6">
					<div className="cathedral-body text-cathedral-brown flex flex-wrap items-center gap-4 text-sm">
						<Link
							to="/tos"
							className="text-cathedral-gold hover:text-cathedral-gold-light underline"
						>
							Terms of Service
						</Link>
						<Link
							to="/"
							className="text-cathedral-gold hover:text-cathedral-gold-light underline"
						>
							Back to Home
						</Link>
					</div>
				</footer>
			</article>
		</main>
	)
}
